import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, useFormikContext } from 'formik'
import { isEqual, get } from 'lodash'
import { Field } from 'formik'
import { uniq, omit, uniqWith } from 'lodash'
import Api from 'services/Api'

import BaseModal from 'components/Modals/BaseModal'
import { closeModal } from 'store/actions/modals'
import { createEvents } from 'store/actions/events'
import {
  DateTimePicker,
  MultiSelect,
  Textarea,
  Button,
  Checkbox,
  SelectScheduleType,
  WagePremiums,
  BillingDetails,
  Attributes,
  FormRow,
  Form
} from 'components/Forms'
import { Loading, Collapsable } from 'components/UI'
import { Breaks, EventSummaryList, IsShiftOver16Hours, UserInput } from 'components/Schedule'
import { Format, Parse, setTime } from 'services/Dates'
import { getAssignablePositions } from 'services/Structure'
import { getScheduleTypeInitialvalue } from 'services/schedule'
import { getProjectsForMultiselect } from 'services/getProjectsForMultiselect'
import { hasAccessToThis } from 'services/Utilities'
import { getParsedTagsForMultiselect } from 'services/getParsedTagsForMultiselect'
import useApi from 'components/Hooks/useApi'
import { Alert, Input, Card } from 'ui'

import { t } from 'translations'

const wagePremiumError = wagePremium => {
  return wagePremium.some(premium => (!!premium.fullShift === false && premium.hours === 0) || (!!premium.fullShift === false && premium.units === 0))
}

const parseEvents = events => {
  if (!Array.isArray(events)) return

  return events.map(event => ({
    ...event,
    startDate: Format(event.start, { outFormat: 'date' }),
    start: Format(event.start, { outFormat: 'serverTime' }),
    end: Format(event.end, { outFormat: 'serverTime' })
  }))
}

const ScheduleMultiEditModal = ({ ids, newEvents }) => {
  const dispatch = useDispatch()
  const { _id: userId } = useSelector(state => state.users.userInfo)
  const { projects, teams, usersById } = useSelector(state => state.resources)
  const {
    enableBillingDetails,
    enableWagePremiums,
    enableShiftSpecificAttributes,
    ignoreReportsToForProjects,
    projectIsRequired,
    teamIsRequired,
    defaultTimeZone: timeZone,
    breaksAreRequired
  } = useSelector(state => state.settings)
  const [errorLoading, setErrorLoading] = useState(false)
  const showOtherInfo = projectIsRequired || teamIsRequired || breaksAreRequired

  // Queries definition
  const {
    isLoading,
    response: oldEventsResponse,
    handleApi: fetchOldEvents
  } = useApi({ method: 'POST', url: 'schedule/schedule/query', initialLoad: !!ids?.length })
  const { handleApi: updateOldEvent } = useApi({ method: 'PUT', url: 'schedule/partial' })
  const { handleApi: updateNewEvents } = useApi({ method: 'POST', url: 'schedule/schedule' })
  // Parse start to time
  const events = oldEventsResponse ? parseEvents(oldEventsResponse) : parseEvents(newEvents)

  useEffect(() => {
    const fetchOldEvent = async () => {
      const { response, isError } = await fetchOldEvents({ shiftIds: ids, showCancelledShift: true })
      if (isError || !response.length) return setErrorLoading(true)
    }
    if (ids && ids.length) fetchOldEvent()
  }, [fetchOldEvents, ids])

  const onSubmit = async ({ sendModificationCancellationEmails, sendNotificationForNewEmployee, ...eventFields }) => {
    // Delete all keys that do not have a value set.
    for (const key in eventFields)
      if (eventFields[key] && (eventFields[key] === 'multipleValues' || eventFields[key].values === 'multipleValues')) delete eventFields[key]

    // Parse project field that is not an array in frontend
    if (!Array.isArray(eventFields.projects) && eventFields.projects && eventFields.projects.length) eventFields.projects = [eventFields.projects]

    // Map all event fields to events and parse them if needed
    let parsedEvents = events.map(event => {
      let { start, end, startDate, ...restEvent } = event
      let { start: newStart, end: newEnd, startDate: newStartDate, ...restEventFields } = eventFields

      // Parse start/end to time
      const parsedStart = Format(setTime(Parse(newStartDate || startDate, { inFormat: 'date' }), newStart || start))
      const parsedEnd = Format(setTime(Parse(newStartDate || startDate, { inFormat: 'date' }), newEnd || end))

      // Parse the fields with radio buttons
      const radioButtonsFields = {}
      Object.keys(eventFields).forEach(field => {
        // We only handle the fields with radio buttons (objects with editType)
        if (!eventFields?.editType[field]) return
        const isComment = field === 'comment'
        if (!event[field]) event[field] = isComment ? '' : []
        // Update the field value depending on the user selection
        switch (eventFields.editType[field]) {
          case 'replace':
            radioButtonsFields[field] = isComment ? eventFields[field] : Array.isArray(eventFields[field]) ? [...eventFields[field]] : eventFields[field]
            break
          case 'add': {
            const updatedComment = event[field] ? event[field].concat(`\n\n${eventFields[field]}`) : eventFields[field]
            radioButtonsFields[field] = isComment
              ? updatedComment
              : typeof eventFields[field][0] !== 'object'
              ? uniq([...event[field], ...eventFields[field]])
              : uniqWith([...event[field], ...eventFields[field]], (arrVal, othVal) => isEqual(omit(arrVal, ['_id']), omit(othVal, ['_id'])))
            break
          }
          case 'remove': {
            const valuesToRemove = typeof eventFields[field][0] === 'object' ? eventFields[field].map(value => omit(value, ['_id'])) : eventFields[field]
            radioButtonsFields[field] = [...event[field]].filter(
              el => !valuesToRemove.some(value => isEqual(value, typeof el === 'object' ? omit(el, ['_id']) : el))
            )
            break
          }
          default:
            return
        }
      })

      return { ...restEvent, ...restEventFields, start: parsedStart, end: parsedEnd, ...radioButtonsFields }
    })

    if (newEvents) {
      const { response } = await updateNewEvents({ event: parsedEvents })
      // Add the shift to redux
      dispatch(createEvents(response))
    } else {
      const options = {
        sendModificationEmails: sendModificationCancellationEmails,
        sendCancellationEmails: sendModificationCancellationEmails
      }
      await updateOldEvent({ events: parsedEvents, options })
    }
    // Send a notification to the newly assigned employee
    if (sendNotificationForNewEmployee) {
      const newEventsForEmployee = events.filter(event => event.employeeId !== eventFields.employeeId)
      const body = {
        shiftIds: newEventsForEmployee.map(event => event._id),
        options: { confirm: true, reconfirm: true, type: 'confirmation' }
      }
      await Api.post('schedule/confirm', body)
    }
    dispatch(closeModal())
  }

  if (isLoading) return <Loading />

  // Function that sets the initial value of the form by checking if all the events have the same value
  const getInitialFieldValue = (field, events) => {
    const getFieldValue = fieldValue => (Array.isArray(fieldValue) ? fieldValue.join(',') : fieldValue)
    const baseValue = getFieldValue(events[0][field])
    const allSame = events.every(event => isEqual(getFieldValue(event[field]), baseValue))

    // Handle the fact that projects is an array in the backend
    if (field === 'projects') return allSame ? get(events[0], 'projects[0]', undefined) : 'multipleValues'
    else return allSame ? events[0][field] : 'multipleValues'
  }

  const MultipleValuesInput = ({
    children,
    className,
    name = '',
    values,
    setFieldValue,
    module = 'manager.schedule.eventForm',
    withRadioButtons,
    disabled
  }) => {
    if (values[name] !== 'multipleValues') return <div className={`${className} p-2`}>{children}</div>

    return (
      <div className="form-group">
        <div className="d-flex flex-column">
          <label className="control-label">{t(`${module}.${name.toUpperCase()}.LABEL`)}</label>
        </div>
        <div className="input-group mb-3">
          <Input disabled type="text" placeholder={t('manager.schedule.eventForm.MULTIPLE_VALUES')} />
          <div className="input-group-append">
            <Button
              className="btn btn-outline-secondary"
              text="manager.schedule.eventForm.CLICK_TO_EDIT"
              onClick={() => {
                // Handle the fact that projects is an array in the backend
                const valueToSet = name === 'projects' ? get(events[0], 'projects[0]', undefined) : events[0][name]
                // Set the value of the field to an object if the field has radio buttons
                return withRadioButtons ? setFieldValue(name, name === 'comment' ? '' : []) : setFieldValue(name, valueToSet ?? null)
              }}
              disabled={disabled}
            />
          </div>
        </div>

        <small className="form-text text-muted">{t(`${module}.${name.toUpperCase()}.DESCRIPTION`)}</small>
      </div>
    )
  }

  return (
    <BaseModal hideButtons title="manager.schedule.MULTI_EDIT_MODAL">
      {errorLoading ? (
        <>
          <Alert variant="warning" className="mt-2">
            {t('manager.schedule.MULTI_EDIT_MODAL.ERROR_LOADING')}
          </Alert>
          <Button text="actions.CLOSE" className="btn btn-outline-secondary mt-2" onClick={() => dispatch(closeModal())} />
        </>
      ) : (
        <Formik
          enableReinitialize={true}
          validate={({ start, end, positionId, breaks, wagePremiums, employeeId }) => {
            let errors = {}
            if (!positionId) errors.positionId = 'manager.schedule.REQUIRED_FIELD'
            if (!start) errors.start = 'manager.schedule.REQUIRED_FIELD'
            if (!end) errors.end = 'manager.schedule.REQUIRED_FIELD'
            if (breaksAreRequired && !breaks) errors.breaks = 'manager.schedule.REQUIRED_FIELD'
            if (breaksAreRequired && breaks && breaks.length === 0) errors.breaks = 'manager.schedule.REQUIRED_FIELD'
            if (wagePremiumError(wagePremiums)) errors.wagePremiums = 'administration.wagePremiums.form.GREATER_THAN_ZERO'
            if (usersById[employeeId]?.archived) errors.employeeId = 'manager.schedule.USER_IS_ARCHIVED'
            return errors
          }}
          initialValues={{
            employeeId: getInitialFieldValue('employeeId', events),
            positionId: getInitialFieldValue('positionId', events),
            startDate: getInitialFieldValue('startDate', events),
            start: getInitialFieldValue('start', events),
            end: getInitialFieldValue('end', events),
            startTimezone: getInitialFieldValue('startTimezone', events) || timeZone,
            endTimezone: getInitialFieldValue('endTimezone', events) || timeZone,
            comment: '',
            isExtraShift: getInitialFieldValue('isExtraShift', events),
            isOvertime: getInitialFieldValue('isOvertime', events),
            isOnCall: getInitialFieldValue('isOnCall', events),
            isIrreplaceable: getInitialFieldValue('isIrreplaceable', events),
            needsReplacement: getInitialFieldValue('needsReplacement', events),
            coveredOtherUnit: getInitialFieldValue('coveredOtherUnit', events),
            noEmployeeAvailable: getInitialFieldValue('noEmployeeAvailable', events),
            hasSpecificRequirements: getInitialFieldValue('hasSpecificRequirements', events),
            projects: getInitialFieldValue('projects', events),
            team: getInitialFieldValue('team', events),
            tags: [],
            breaks: [],
            wagePremiums: [],
            billingDetails: getInitialFieldValue('billingDetails', events) || { billingId: '' },
            requiredAttributes: [],
            sendModificationCancellationEmails: false,
            sendNotificationForNewEmployee: false,
            selectedScheduleType: getScheduleTypeInitialvalue(events),
            editType: {
              tags: 'add',
              breaks: 'add',
              wagePremiums: 'add',
              requiredAttributes: 'add',
              comment: 'add'
            }
          }}
          onSubmit={values => onSubmit(values)}
        >
          {({ values, initialValues, status, setStatus, isSubmitting, setFieldValue }) => {
            // Props for the multiple values component
            const mvProps = { values, setFieldValue }
            // Detect if positionId, project,comment or start/end have changed
            const hasProjectChanged = values.projects !== initialValues.projects
            const hasPositionChanged = values.positionId !== initialValues.positionId
            const hasTimeChanged = values.start !== initialValues.start || values.end !== initialValues.end
            const hasCommentChanged = values.comment !== initialValues.comment
            const hasStartDateChanged = values.startDate !== initialValues.startDate
            const employeeHaschanged = values.employeeId !== initialValues.employeeId
            const importantChanges = hasProjectChanged || hasPositionChanged || hasTimeChanged || hasCommentChanged || hasStartDateChanged || employeeHaschanged

            return (
              <Form>
                {/* Error message */}
                <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                  {status?.msg}
                </Alert>
                {!newEvents ? (
                  <div className="mb-2">
                    <EventSummaryList events={events} isTime />
                  </div>
                ) : null}
                <IsShiftOver16Hours {...values} />

                {/* Form */}
                {/* Main info */}
                <MultipleValuesInput name="employeeId" {...mvProps} disabled={!hasAccessToThis('scheduleAssignEmployee')}>
                  <UserInput disabled={!hasAccessToThis('scheduleAssignEmployee')} />
                </MultipleValuesInput>
                <MultipleValuesInput name="positionId" {...mvProps}>
                  <MultiSelect
                    module="manager.schedule.eventForm"
                    name="positionId"
                    options={getAssignablePositions({ forMultiSelect: true, projectId: values.projects })}
                    required
                  />
                </MultipleValuesInput>

                {/* TIME section */}
                <FormRow>
                  <MultipleValuesInput name="start" {...mvProps}>
                    <DateTimePicker module="manager.schedule.eventForm" name="start" type="time" required />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="end" {...mvProps}>
                    <DateTimePicker module="manager.schedule.eventForm" name="end" type="time" required />
                  </MultipleValuesInput>
                </FormRow>
                <FormRow>
                  <MultipleValuesInput name="startDate" {...mvProps}>
                    <DateTimePicker module="manager.schedule.eventForm" name="startDate" type="date" required />
                  </MultipleValuesInput>
                  {/* MultiSelect for selecting shift type */}
                  <MultipleValuesInput name="selectedScheduleType" {...mvProps}>
                    <SelectScheduleType shifts={events} shift={values} setFieldValue={setFieldValue} />
                  </MultipleValuesInput>
                </FormRow>

                <MultipleValuesInput name="comment" {...mvProps} withRadioButtons>
                  <Textarea module="manager.schedule.eventForm" name="comment" />
                  <ToggleEdit name="editType.comment" />
                </MultipleValuesInput>

                {/* Shift options */}
                <Collapsable type="shiftOptions" title="manager.schedule.eventForm.SHIFT_OPTIONS">
                  <MultipleValuesInput name="isExtraShift" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="isExtraShift" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="isOvertime" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="isOvertime" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="isOnCall" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="isOnCall" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="isIrreplaceable" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="isIrreplaceable" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="needsReplacement" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="needsReplacement" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="noEmployeeAvailable" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="noEmployeeAvailable" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="hasSpecificRequirements" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="hasSpecificRequirements" />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="coveredOtherUnit" {...mvProps}>
                    <Checkbox module="manager.schedule.eventForm" name="coveredOtherUnit" />
                  </MultipleValuesInput>
                </Collapsable>

                {/* Extra info */}
                <Collapsable type="otherInfo" title="manager.schedule.eventForm.OTHER_INFO" show={showOtherInfo}>
                  <MultipleValuesInput name="projects" {...mvProps}>
                    <MultiSelect
                      module="manager.schedule.eventForm"
                      name="projects"
                      options={getProjectsForMultiselect(projects, {
                        ignoreReportsToForProjects,
                        permission: !!hasAccessToThis('limitToReportsTo'),
                        connectedUser: userId,
                        t
                      })}
                      required={projectIsRequired}
                      isClearable
                      defaultValue={''}
                      isOptionDisabled={({ isDisabled }) => isDisabled}
                    />
                  </MultipleValuesInput>
                  <MultipleValuesInput name="team" {...mvProps}>
                    <MultiSelect
                      module="manager.schedule.eventForm"
                      name="team"
                      options={teams.map(({ _id: value, name: label, reportsTo = [] }) => {
                        const canAssignTeam = !hasAccessToThis('limitToReportsTo') || reportsTo.includes(userId)
                        return { label, value, isHidden: !canAssignTeam }
                      })}
                      required={teamIsRequired}
                      isClearable
                      defaultValue={''}
                    />
                  </MultipleValuesInput>
                  <Card>
                    <MultipleValuesInput name="tags" {...mvProps} withRadioButtons isMulti>
                      <MultiSelect module="manager.schedule.eventForm" name="tags" options={getParsedTagsForMultiselect()} isClearable isMulti />
                      <ToggleEdit name="editType.tags" remove />
                    </MultipleValuesInput>
                  </Card>

                  <Card>
                    <MultipleValuesInput className="mt-3" name="breaks" {...mvProps} withRadioButtons isMulti>
                      <Breaks
                        event={{ start: values.start, end: values.end, positionId: values.positionId }}
                        breaks={values.breaks}
                        required={breaksAreRequired}
                      />
                      <ToggleEdit name="editType.breaks" remove />
                    </MultipleValuesInput>
                  </Card>

                  {enableWagePremiums ? (
                    <Card>
                      <MultipleValuesInput className="mt-3" name="wagePremiums" {...mvProps} withRadioButtons isMulti>
                        <WagePremiums wagePremiums={values.wagePremiums} />
                        <ToggleEdit name="editType.wagePremiums" remove />
                      </MultipleValuesInput>
                    </Card>
                  ) : null}

                  {enableShiftSpecificAttributes ? (
                    <Card>
                      <MultipleValuesInput className="mt-3" name="requiredAttributes" {...mvProps} withRadioButtons isMulti>
                        <Attributes module="manager.schedule.eventForm" name="requiredAttributes" attributes={values.requiredAttributes} />
                        <ToggleEdit name="editType.requiredAttributes" remove />
                      </MultipleValuesInput>
                    </Card>
                  ) : null}
                </Collapsable>

                {!!enableBillingDetails && (
                  <Collapsable type="billingDetails" title="components.billingDetails.BILLING_DETAILS">
                    <MultipleValuesInput module="components.billingDetails" name="billingDetails" {...mvProps}>
                      <BillingDetails context="shift" />
                    </MultipleValuesInput>
                  </Collapsable>
                )}

                {importantChanges || employeeHaschanged ? (
                  <ChangeComponent employeeHaschanged={employeeHaschanged} importantChanges={importantChanges} />
                ) : null}
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                  {t(`actions.CLOSE`)}
                </Button>
              </Form>
            )
          }}
        </Formik>
      )}
    </BaseModal>
  )
}

const ChangeComponent = ({ employeeHaschanged, importantChanges }) => {
  // Grab setFieldValue from context
  const { setFieldValue } = useFormikContext()
  useEffect(() => {
    if (importantChanges) {
      setFieldValue('sendModificationCancellationEmails', true)
    } else {
      setFieldValue('sendModificationCancellationEmails', false)
    }
    if (employeeHaschanged) {
      setFieldValue('sendNotificationForNewEmployee', true)
    } else {
      setFieldValue('sendNotificationForNewEmployee', false)
    }
  }, [employeeHaschanged, importantChanges, setFieldValue])
  return (
    <>
      <Card className="bg-light mb-2">
        <Card.Body className="pb-0">
          {/* Whether or not to send a modification email */}
          {importantChanges ? <Checkbox module="manager.schedule.MULTI_EDIT_MODAL" name="sendModificationCancellationEmails" /> : null}
          {/* Send a notice to the newly assigned employee */}
          {employeeHaschanged ? <Checkbox module="manager.schedule.eventForm" name="sendNotificationForNewEmployee" /> : null}
        </Card.Body>
      </Card>
    </>
  )
}

export default ScheduleMultiEditModal

const ToggleEdit = ({ name, remove = false }) => (
  <div className=" d-flex" role="group" aria-labelledby="toggleEdit">
    <label className="mr-2">
      <Field type="radio" name={name} value="replace" />
      {t('manager.schedule.eventForm.REPLACE')}
    </label>
    <label className="mr-2">
      <Field type="radio" name={name} value="add" />
      {t('manager.schedule.eventForm.ADD')}
    </label>
    {!!remove && (
      <label>
        <Field type="radio" name={name} value="remove" />
        {t('manager.schedule.eventForm.REMOVE')}
      </label>
    )}
  </div>
)
